import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
      Area,
      AreaChart,
      CartesianGrid,
      ResponsiveContainer,
      Tooltip,
      type TooltipProps,
      XAxis,
      YAxis,
} from "recharts";
import { ChartAreaSkeleton } from "@/components/loading";
import { SENTIMENT_COLORS, resolveCssColor } from "@/lib/chart-colors";
import { getDisplayPeriodQuery } from "@/lib/display-period";
import { EMPTY_CHART_MESSAGE } from "@/lib/empty-state-copy";
import type { SearchExecutionActivity } from "@/lib/search-activity";
import { useTRPC } from "@/lib/trpc";
import type { BrandDashboardPeriod } from "@/state/store/userPreferences";
import type { BaseChartConfig, ChartType } from "../dashboard/base-chart";
import { BaseChart } from "../dashboard/base-chart";
import { SearchActivityOverlay } from "../search-activity-overlay";
import type { ChartBrand, ChartDataPoint } from "../dashboard/types";
import { MentionRateTooltip } from "../tooltips/mention-rate-tooltip";
import { RankingTooltip } from "../tooltips/ranking-tooltip";
import { SentimentScoreTooltip } from "../tooltips/sentiment-score-tooltip";
import { VisibilityScoreTooltip } from "../tooltips/visibility-score-tooltip";
import { Card, CardContent, CardTitle } from "../ui/card";

interface SearchSpecificationChartProps {
      searchSpecificationId: string;
      period: BrandDashboardPeriod;
      activity?: SearchExecutionActivity | null;
      chartType?: ChartType;
}

const noBatchRequest = { trpc: { context: { noBatch: true } } } as const;
// ============================================================================
// VISIBILITY SCORE CHART - Shows main brand visibility for search specification
// ============================================================================

const visibilityConfig: BaseChartConfig = {
      title: "Visibility Score",
      tooltip: <VisibilityScoreTooltip />,
      separatedHeader: true,
      // description: "Your brand visibility over time",
      yAxisLabel: "Score",
      yAxisDomain: [0, 100],
      valueSuffix: "",
      showSeriesToggles: false,
      chartType: "area",
      showTrend: true,
};

export const SearchSpecificationVisibilityChart = ({
      searchSpecificationId,
      period,
      activity = null,
      chartType,
}: SearchSpecificationChartProps) => {
      const trpc = useTRPC();

      const { data: chartResponse, isLoading } = useQuery(
            trpc.charts.getSearchSpecificationVisibilityChartData.queryOptions({
                  searchSpecificationId,
                  ...getDisplayPeriodQuery(period),
            }, noBatchRequest),
      );

      const chartData = useMemo<ChartDataPoint[]>(() => {
            if (!chartResponse?.chartData) return [];
            return chartResponse.chartData.map((point) => {
                  const dataPoint: ChartDataPoint = {
                        date: point.date,
                        label: point.label,
                        intervalLabel: point.intervalLabel,
                  };
                  point.brandScores.forEach((brandScore) => {
                        dataPoint[brandScore.brandId] = brandScore.score;
                  });
                  return dataPoint;
            });
      }, [chartResponse]);

      const brands = useMemo<ChartBrand[]>(() => {
            return (chartResponse?.brands ?? []).map((brand) => ({
                  id: brand.id,
                  name: brand.name,
                  logo: brand.logo,
                  isMainBrand: brand.isMainBrand,
            }));
      }, [chartResponse?.brands]);

      const config = useMemo(
            () => ({
                  ...visibilityConfig,
                  chartType: chartType ?? visibilityConfig.chartType,
            }),
            [chartType],
      );

      return (
            <BaseChart
                  config={config}
                  chartData={chartData}
                  brands={brands}
                  isLoading={isLoading}
                  activity={activity}
            />
      );
};

// ============================================================================
// MENTION RATE CHART - Shows mention rate for main brand
// ============================================================================

const mentionConfig: BaseChartConfig = {
      title: "Mention Rate",
      tooltip: <MentionRateTooltip />,
      separatedHeader: true,
      // description: "How often your brand is mentioned",
      yAxisLabel: "Mention %",
      yAxisDomain: [0, 100],
      valueSuffix: "%",
      showSeriesToggles: false,
      chartType: "area",
      showTrend: true,
};

export const SearchSpecificationMentionChart = ({
      searchSpecificationId,
      period,
      activity = null,
      chartType,
}: SearchSpecificationChartProps) => {
      const trpc = useTRPC();

      const { data: chartResponse, isLoading } = useQuery(
            trpc.charts.getSearchSpecificationMentionChartData.queryOptions({
                  searchSpecificationId,
                  ...getDisplayPeriodQuery(period),
            }, noBatchRequest),
      );

      const chartData = useMemo<ChartDataPoint[]>(() => {
            if (!chartResponse?.chartData) return [];
            return chartResponse.chartData.map((point) => {
                  const dataPoint: ChartDataPoint = {
                        date: point.date,
                        label: point.label,
                        intervalLabel: point.intervalLabel,
                  };
                  // Extract percentage for each brand
                  Object.entries(point.brandMentions).forEach(([brandId, data]) => {
                        dataPoint[brandId] = data.percentage;
                  });
                  return dataPoint;
            });
      }, [chartResponse]);

      const brands = useMemo<ChartBrand[]>(() => {
            return (chartResponse?.brands ?? []).map((brand) => ({
                  id: brand.id,
                  name: brand.name,
                  logo: brand.logo,
                  isMainBrand: brand.isMainBrand,
            }));
      }, [chartResponse?.brands]);

      const config = useMemo(
            () => ({
                  ...mentionConfig,
                  chartType: chartType ?? mentionConfig.chartType,
            }),
            [chartType],
      );

      return (
            <BaseChart
                  config={config}
                  chartData={chartData}
                  brands={brands}
                  isLoading={isLoading}
                  activity={activity}
            />
      );
};

// ============================================================================
// SENTIMENT DISTRIBUTION CHART - Stacked area chart showing sentiment breakdown
// Negative: < 60 (normalized), Neutral: 60-80, Positive: > 80
// ============================================================================

// Sentiment colors from CSS variables
const POSITIVE_COLOR = SENTIMENT_COLORS.positive;
const NEUTRAL_COLOR = SENTIMENT_COLORS.neutral;
const NEGATIVE_COLOR = SENTIMENT_COLORS.negative;

interface SentimentChartDataPoint {
      date: string;
      label: string;
      intervalLabel?: string;
      positivePercentage: number;
      neutralPercentage: number;
      negativePercentage: number;
      totalResponses: number;
}

// Custom tooltip for sentiment distribution
const SentimentTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
      if (!active || !payload || payload.length === 0) return null;

      const data = payload[0]?.payload as SentimentChartDataPoint | undefined;
      if (!data) return null;

      return (
            <div className="bg-popover border border-border rounded-lg shadow-lg p-3 min-w-[160px]">
                  <div className="text-sm font-medium mb-2">{data.intervalLabel ?? label}</div>
                  <div className="space-y-1.5">
                        <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-2">
                                    <div
                                          className="w-2 h-2 rounded-full shrink-0"
                                          style={{ backgroundColor: POSITIVE_COLOR }}
                                    />
                                    <span className="text-sm text-foreground">Positive</span>
                              </div>
                              <span className="text-sm font-medium tabular-nums">
                                    {data.positivePercentage?.toFixed(1) ?? 0}%
                              </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-2">
                                    <div
                                          className="w-2 h-2 rounded-full shrink-0"
                                          style={{ backgroundColor: NEUTRAL_COLOR }}
                                    />
                                    <span className="text-sm text-foreground">Neutral</span>
                              </div>
                              <span className="text-sm font-medium tabular-nums">
                                    {data.neutralPercentage?.toFixed(1) ?? 0}%
                              </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-2">
                                    <div
                                          className="w-2 h-2 rounded-full shrink-0"
                                          style={{ backgroundColor: NEGATIVE_COLOR }}
                                    />
                                    <span className="text-sm text-foreground">Negative</span>
                              </div>
                              <span className="text-sm font-medium tabular-nums">
                                    {data.negativePercentage?.toFixed(1) ?? 0}%
                              </span>
                        </div>
                        <div className="pt-1 border-t border-border mt-1">
                              <div className="flex items-center justify-between gap-4 text-xs text-muted-foreground">
                                    <span>Total responses</span>
                                    <span>{data.totalResponses ?? 0}</span>
                              </div>
                        </div>
                  </div>
            </div>
      );
};

export const SearchSpecificationSentimentChart = ({
      searchSpecificationId,
      period,
      activity = null,
}: Omit<SearchSpecificationChartProps, "chartType">) => {
      const trpc = useTRPC();
      const showLoadingOverlay = Boolean(activity?.isActive && activity.phase !== "scanning");
      const positiveColor = useMemo(() => resolveCssColor(POSITIVE_COLOR), []);
      const neutralColor = useMemo(() => resolveCssColor(NEUTRAL_COLOR), []);
      const negativeColor = useMemo(() => resolveCssColor(NEGATIVE_COLOR), []);

      const { data: chartResponse, isLoading } = useQuery(
            trpc.charts.getSearchSpecificationSentimentChartData.queryOptions({
                  searchSpecificationId,
                  ...getDisplayPeriodQuery(period),
            }, noBatchRequest),
      );

      const chartData = useMemo<SentimentChartDataPoint[]>(() => {
            if (!chartResponse?.chartData) return [];
            return chartResponse.chartData.map((point) => ({
                  date: point.date,
                  label: point.label,
                  intervalLabel: point.intervalLabel,
                  positivePercentage: point.sentimentDistribution.positivePercentage,
                  neutralPercentage: point.sentimentDistribution.neutralPercentage,
                  negativePercentage: point.sentimentDistribution.negativePercentage,
                  totalResponses: point.sentimentDistribution.totalResponses,
            }));
      }, [chartResponse]);

      // Check if there's any data
      const hasData = chartData.some((point) => point.totalResponses > 0);

      // Generate unique gradient IDs
      const gradientIdPrefix = useMemo(() => `sentiment-gradient-${Math.random().toString(36).substr(2, 9)}`, []);

      return (
            <Card className="w-full h-full flex flex-col overflow-hidden p-0 gap-0">
                  <div className="flex h-14 items-center border-b px-6 shrink-0 bg-muted/40">
                        <div className="flex min-w-0 items-center gap-3">
                              <CardTitle className="text-lg font-semibold">Sentiment Distribution</CardTitle>
                              <SentimentScoreTooltip />
                        </div>
                  </div>
                  <CardContent className="relative flex-1 py-4">
                        {showLoadingOverlay && <SearchActivityOverlay activity={activity} />}

                        {isLoading && (
                              <ChartAreaSkeleton className="border-0 bg-transparent p-0" heightClassName="h-[280px]" />
                        )}

                        {!isLoading && (
                              <div className="w-full" style={{ height: 280 }}>
                                    {!hasData ? (
                                          <div
                                                className="flex items-center justify-center text-muted-foreground"
                                                style={{ height: 280 }}
                                          >
                                    {!showLoadingOverlay && EMPTY_CHART_MESSAGE}
                                          </div>
                                    ) : (
                                          <ResponsiveContainer width="100%" height="100%">
                                                <AreaChart
                                                      data={chartData}
                                                      margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                                                >
                                                      <defs>
                                                            <linearGradient
                                                                  id={`${gradientIdPrefix}-positive`}
                                                                  x1="0"
                                                                  y1="0"
                                                                  x2="0"
                                                                  y2="1"
                                                            >
                                                                  <stop
                                                                        offset="5%"
                                                                        stopColor={positiveColor}
                                                                        stopOpacity={0.8}
                                                                  />
                                                                  <stop
                                                                        offset="95%"
                                                                        stopColor={positiveColor}
                                                                        stopOpacity={0.3}
                                                                  />
                                                            </linearGradient>
                                                            <linearGradient
                                                                  id={`${gradientIdPrefix}-neutral`}
                                                                  x1="0"
                                                                  y1="0"
                                                                  x2="0"
                                                                  y2="1"
                                                            >
                                                                  <stop
                                                                        offset="5%"
                                                                        stopColor={neutralColor}
                                                                        stopOpacity={0.8}
                                                                  />
                                                                  <stop
                                                                        offset="95%"
                                                                        stopColor={neutralColor}
                                                                        stopOpacity={0.3}
                                                                  />
                                                            </linearGradient>
                                                            <linearGradient
                                                                  id={`${gradientIdPrefix}-negative`}
                                                                  x1="0"
                                                                  y1="0"
                                                                  x2="0"
                                                                  y2="1"
                                                            >
                                                                  <stop
                                                                        offset="5%"
                                                                        stopColor={negativeColor}
                                                                        stopOpacity={0.8}
                                                                  />
                                                                  <stop
                                                                        offset="95%"
                                                                        stopColor={negativeColor}
                                                                        stopOpacity={0.3}
                                                                  />
                                                            </linearGradient>
                                                      </defs>
                                                      <CartesianGrid
                                                            vertical={false}
                                                            strokeDasharray="3 3"
                                                            className="stroke-muted/15"
                                                      />
                                                      <XAxis
                                                            dataKey="label"
                                                            tickLine={false}
                                                            axisLine={false}
                                                            tickMargin={8}
                                                            className="text-xs"
                                                            tick={{ fill: "var(--muted-foreground)" }}
                                                      />
                                                      <YAxis
                                                            tickLine={false}
                                                            axisLine={false}
                                                            tickMargin={8}
                                                            className="text-xs"
                                                            tick={{ fill: "var(--muted-foreground)" }}
                                                            domain={[0, 100]}
                                                            width={50}
                                                            tickFormatter={(value) => `${value}%`}
                                                      />
                                                      <Tooltip
                                                            content={<SentimentTooltip />}
                                                            cursor={{
                                                                  stroke: "var(--muted-foreground)",
                                                                  strokeWidth: 1,
                                                                  strokeDasharray: "4 4",
                                                            }}
                                                      />
                                                      {/* Stack order: negative at bottom, neutral in middle, positive on top */}
                                                      <Area
                                                            type="monotone"
                                                            dataKey="negativePercentage"
                                                            stackId="1"
                                                            stroke={negativeColor}
                                                            strokeWidth={1}
                                                            fill={`url(#${gradientIdPrefix}-negative)`}
                                                            name="Negative"
                                                      />
                                                      <Area
                                                            type="monotone"
                                                            dataKey="neutralPercentage"
                                                            stackId="1"
                                                            stroke={neutralColor}
                                                            strokeWidth={1}
                                                            fill={`url(#${gradientIdPrefix}-neutral)`}
                                                            name="Neutral"
                                                      />
                                                      <Area
                                                            type="monotone"
                                                            dataKey="positivePercentage"
                                                            stackId="1"
                                                            stroke={positiveColor}
                                                            strokeWidth={1}
                                                            fill={`url(#${gradientIdPrefix}-positive)`}
                                                            name="Positive"
                                                      />
                                                </AreaChart>
                                          </ResponsiveContainer>
                                    )}
                              </div>
                        )}
                  </CardContent>
            </Card>
      );
};

// ============================================================================
// RANKING CHART - Shows ranking position for main brand
// ============================================================================

const rankingConfig: BaseChartConfig = {
      title: "Ranking",
      separatedHeader: true,
      // description: "Your brand ranking position",
      yAxisLabel: "Position",
      yAxisDomain: [1, 10],
      yAxisReversed: true, // Lower is better, so 1 should be at top
      treatNullAsNoData: true,
      showSeriesToggles: false,
      chartType: "line",
      showTrend: true,
      dataSeries: [{ suffix: "", label: "", strokeWidth: 1.5, dotRadius: 3, strokeDasharray: "5 3" }],
      valueFormatter: (value) => {
            if (value === null) return "N/A";
            return `#${value.toFixed(1)}`;
      },
};

export const SearchSpecificationRankingChart = ({
      searchSpecificationId,
      period,
      activity = null,
      chartType,
}: SearchSpecificationChartProps) => {
      const trpc = useTRPC();

      const { data: chartResponse, isLoading } = useQuery(
            trpc.charts.getSearchSpecificationRankingChartData.queryOptions({
                  searchSpecificationId,
                  ...getDisplayPeriodQuery(period),
            }, noBatchRequest),
      );

      const chartData = useMemo<ChartDataPoint[]>(() => {
            if (!chartResponse?.chartData) return [];
            return chartResponse.chartData.map((point) => {
                  const dataPoint: ChartDataPoint = {
                        date: point.date,
                        label: point.label,
                        intervalLabel: point.intervalLabel,
                  };
                  // Extract avg ranking for each brand
                  Object.entries(point.brandRankings).forEach(([brandId, data]) => {
                        dataPoint[brandId] = data.avg;
                  });
                  return dataPoint;
            });
      }, [chartResponse]);

      const brands = useMemo<ChartBrand[]>(() => {
            return (chartResponse?.brands ?? []).map((brand) => ({
                  id: brand.id,
                  name: brand.name,
                  logo: brand.logo,
                  isMainBrand: brand.isMainBrand,
            }));
      }, [chartResponse?.brands]);

      const config = useMemo(
            () => ({
                  ...rankingConfig,
                  chartType: chartType ?? rankingConfig.chartType,
            }),
            [chartType],
      );

      return (
            <BaseChart
                  config={{
                        ...config,
                        tooltip: <RankingTooltip />,
                  }}
                  chartData={chartData}
                  brands={brands}
                  isLoading={isLoading}
                  activity={activity}
            />
      );
};
